import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import Loader from '@client/components/loader/Loader';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import { endSession } from './endSession';
import { GATE_VIEW, getGateView } from './gateView';
import { getPortalConfig } from './portals';
import { ensureProfile } from './session';
import { useSession } from './useSession';
import styles from './requireAuth.scss';

function GateMessage({ title, text, actionLabel, onAction }) {
  return (
    <section className={styles.gateMessage}>
      <h1 className={styleHelper.text1}>{title}</h1>
      <p className={styleHelper.text5}>{text}</p>
      <button
        type="button"
        className={styleHelper.btnContained}
        onClick={onAction}
      >
        {actionLabel}
      </button>
    </section>
  );
}

GateMessage.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
};

// The Auth Gate: the page underneath doesn't mount until the Session is
// restored and the user's profile is loaded for this Portal. `onProfileLoaded`
// runs before the page mounts, so the page can read the profile right away.
function RequireAuth({ portal, onProfileLoaded, children }) {
  const session = useSession();
  const [syncedProfile, setSyncedProfile] = useState(null);
  const view = getGateView(session, portal);

  useEffect(() => {
    ensureProfile(portal);
  }, [portal]);

  useEffect(() => {
    if (view === GATE_VIEW.CONTENT && session.profile !== syncedProfile) {
      onProfileLoaded(session.profile);
      setSyncedProfile(session.profile);
    }
  }, [view, session.profile, syncedProfile, onProfileLoaded]);

  switch (view) {
    case GATE_VIEW.REDIRECT_TO_LOGIN:
      return <Redirect to={getPortalConfig(portal).loginPath} />;
    case GATE_VIEW.ACCESS_DENIED:
      return (
        <GateMessage
          title="Access denied"
          text={`Your account does not have access to the ${portal} portal. Sign out to switch accounts.`}
          actionLabel="Sign out"
          onAction={() => endSession(portal)}
        />
      );
    case GATE_VIEW.UNAVAILABLE:
      return (
        <GateMessage
          title="Temporarily unavailable"
          text="We can't reach the service right now. Please try again in a moment."
          actionLabel="Try again"
          onAction={() => window.location.reload()}
        />
      );
    case GATE_VIEW.CONTENT:
      return session.profile === syncedProfile ? children : <Loader />;
    default:
      return <Loader />;
  }
}

RequireAuth.propTypes = {
  portal: PropTypes.string.isRequired,
  onProfileLoaded: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default RequireAuth;
